/**
 * Allergen columns of an allergy matrix. The ten standard columns are always
 * present (the nine major US allergens plus alcohol); a matrix may add up to
 * `MAX_CUSTOM_ALLERGENS` custom columns (pork, mustard, ...), keyed
 * `custom_<slug>`. Rows store the keys they contain, never labels, so a
 * standard column's label comes from translations
 * (`allergy-matrix.allergens.<key>`).
 */
export const STANDARD_ALLERGENS = [
  'eggs',
  'dairy',
  'peanuts',
  'tree_nuts',
  'fish',
  'shellfish',
  'gluten',
  'soy',
  'sesame',
  'alcohol'
] as const;

export type StandardAllergen = (typeof STANDARD_ALLERGENS)[number];

export type CustomAllergen = { key: string; label: string };

export const CUSTOM_ALLERGEN_PREFIX = 'custom_';
export const MAX_CUSTOM_ALLERGENS = 20;
export const MAX_ALLERGEN_LABEL_LENGTH = 40;

/** Column limits shared by the ents, the mutator schemas and the UI. */
export const MAX_MATRIX_NAME_LENGTH = 120;
export const MAX_MATRIX_FOOTNOTES_LENGTH = 2000;
export const MAX_SECTION_NAME_LENGTH = 120;
export const MAX_ROW_NAME_LENGTH = 200;
export const MAX_ROW_NOTE_LENGTH = 500;
export const MAX_MATRIX_ROWS = 300;

const CUSTOM_KEY_PATTERN = /^custom_[a-z0-9_]{1,40}$/;

export function isStandardAllergen(key: string): key is StandardAllergen {
  return (STANDARD_ALLERGENS as readonly string[]).includes(key);
}

export function isCustomAllergenKey(key: string): boolean {
  return CUSTOM_KEY_PATTERN.test(key);
}

export function isAllergenKey(key: string): boolean {
  return isStandardAllergen(key) || isCustomAllergenKey(key);
}

/** `custom_<slug>` for a label, or `null` when nothing usable is left. */
export function customAllergenKey(label: string): string | null {
  const slug = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
    .replace(/_+$/g, '');
  return slug ? `${CUSTOM_ALLERGEN_PREFIX}${slug}` : null;
}

/** Synonyms a printed matrix uses for the standard columns. */
const SYNONYMS: Record<string, StandardAllergen> = {
  egg: 'eggs',
  eggs: 'eggs',
  dairy: 'dairy',
  milk: 'dairy',
  lactose: 'dairy',
  peanut: 'peanuts',
  peanuts: 'peanuts',
  'tree nut': 'tree_nuts',
  'tree nuts': 'tree_nuts',
  treenuts: 'tree_nuts',
  nuts: 'tree_nuts',
  fish: 'fish',
  shellfish: 'shellfish',
  crustacean: 'shellfish',
  crustaceans: 'shellfish',
  mollusc: 'shellfish',
  molluscs: 'shellfish',
  mollusk: 'shellfish',
  mollusks: 'shellfish',
  gluten: 'gluten',
  wheat: 'gluten',
  soy: 'soy',
  soya: 'soy',
  soybean: 'soy',
  soybeans: 'soy',
  sesame: 'sesame',
  alcohol: 'alcohol'
};

/** The standard column a printed header names, if any. */
export function standardAllergenFor(label: string): StandardAllergen | null {
  const normalized = label
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z ]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return SYNONYMS[normalized] ?? null;
}

/** Parses a stored JSON string array of keys; bad input reads as empty. */
export function parseAllergenKeys(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter(
          (key): key is string => typeof key === 'string' && isAllergenKey(key)
        )
      : [];
  } catch {
    return [];
  }
}

/** Parses stored custom column definitions; bad input reads as empty. */
export function parseCustomAllergens(
  value: string | null | undefined
): CustomAllergen[] {
  if (!value) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (entry): entry is CustomAllergen =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as CustomAllergen).key === 'string' &&
        typeof (entry as CustomAllergen).label === 'string' &&
        isCustomAllergenKey((entry as CustomAllergen).key)
    );
  } catch {
    return [];
  }
}

/** Keys in column order: standard first, then the matrix's custom ones. */
export function orderAllergenKeys(
  keys: Iterable<string>,
  customAllergens: readonly CustomAllergen[]
): string[] {
  const wanted = new Set(keys);
  return [
    ...STANDARD_ALLERGENS,
    ...customAllergens.map(column => column.key)
  ].filter(key => wanted.has(key));
}
